import { useEffect, useState, useRef } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Sparkles,
  HelpCircle,
  Users,
  BookOpen,
  Disc3,
  UserCheck,
  LayoutGrid,
  Menu,
  X,
  Compass,
  Gift,
  FileText,
  Edit3,
  Globe,
  ChevronDown,
  QrCode,
  Calculator,
  HelpCircle as QuestionIcon
} from "lucide-react";
import type { ReactNode } from "react";
import { relatedTools, type ToolMeta } from "@/lib/tools-catalog";
import { relatedEsTools, type EsToolMeta, getEsSlugForEnSlug, getEnSlugForEsSlug } from "@/lib/es-tools-catalog";
import { relatedDeTools, type DeToolMeta, getDeSlugForEnSlug, getEnSlugForDeSlug } from "@/lib/de-tools-catalog";
import { relatedPtBrTools, type PtBrToolMeta, getPtBrSlugForEnSlug, getEnSlugForPtBrSlug } from "@/lib/pt-br-tools-catalog";
import { relatedFrTools, type FrToolMeta, getFrSlugForEnSlug, getEnSlugForFrSlug } from "@/lib/fr-tools-catalog";
import { getEsPdfSlugForEnSlug, getEnPdfSlugForEsSlug } from "@/lib/es-pdf-catalog";
import { getDePdfSlugForEnSlug, getEnPdfSlugForDeSlug } from "@/lib/de-pdf-catalog";
import { getPtBrPdfSlugForEnSlug, getEnPdfSlugForPtBrSlug } from "@/lib/pt-br-pdf-catalog";
import { getFrPdfSlugForEnSlug, getEnPdfSlugForFrSlug } from "@/lib/fr-pdf-catalog";
import { getEsCalculatorSlugForEnSlug, getEnCalculatorSlugForEsSlug } from "@/lib/es-calculators-catalog";
import { getDeCalculatorSlugForEnSlug, getEnCalculatorSlugForDeSlug } from "@/lib/de-calculators-catalog";
import { getPtBrCalculatorSlugForEnSlug, getEnCalculatorSlugForPtBrSlug } from "@/lib/pt-br-calculators-catalog";
import { getFrCalculatorSlugForEnSlug, getEnCalculatorSlugForFrSlug } from "@/lib/fr-calculators-catalog";
import type { ToolContent } from "@/lib/tool-content";
import { PicaminiLogo } from "@/components/ui/Logo";
import { getTranslation, type Language } from "@/lib/i18n";
import { CalculatorsMegaMenu, CalculatorsMobileMenu } from "@/components/calculators/CalculatorsMegaMenu";
import { PdfMegaMenu, PdfMobileMenu } from "@/components/tools/PdfMegaMenu";

export function getEquivalentUrl(currentPath: string, targetLang: Language): string {
  const cleanPath = currentPath.split("?")[0].split("#")[0];
  const path = cleanPath.replace(/\/$/, "") || "/";

  let enBasePath = path;

  if (path.startsWith("/de")) {
    const dePath = path.replace(/^\/de/, "") || "/";
    if (dePath === "/" || dePath === "") {
      enBasePath = "/";
    } else if (dePath === "/calculators") {
      enBasePath = "/calculators";
    } else if (dePath.startsWith("/calculators/")) {
      const deSlug = dePath.replace("/calculators/", "");
      enBasePath = `/calculators/${getEnCalculatorSlugForDeSlug(deSlug)}`;
    } else if (dePath === "/pdf-tools") {
      enBasePath = "/pdf-tools";
    } else if (dePath.startsWith("/pdf-tools/")) {
      const deSlug = dePath.replace("/pdf-tools/", "");
      enBasePath = `/pdf-tools/${getEnPdfSlugForDeSlug(deSlug)}`;
    } else if (dePath === "/ueber-uns" || dePath === "/über-uns") {
      enBasePath = "/about";
    } else if (dePath === "/kontakt") {
      enBasePath = "/contact";
    } else if (dePath === "/datenschutzrichtlinie") {
      enBasePath = "/privacy-policy";
    } else if (dePath === "/nutzungsbedingungen") {
      enBasePath = "/terms-of-use";
    } else if (dePath === "/haftungsausschluss") {
      enBasePath = "/disclaimer";
    } else {
      const deSlug = dePath.replace(/^\//, "");
      enBasePath = `/${getEnSlugForDeSlug(deSlug)}`;
    }
  } else if (path.startsWith("/es")) {
    const esPath = path.replace(/^\/es/, "") || "/";
    if (esPath === "/" || esPath === "") {
      enBasePath = "/";
    } else if (esPath === "/calculators") {
      enBasePath = "/calculators";
    } else if (esPath.startsWith("/calculators/")) {
      const esSlug = esPath.replace("/calculators/", "");
      enBasePath = `/calculators/${getEnCalculatorSlugForEsSlug(esSlug)}`;
    } else if (esPath === "/pdf-tools") {
      enBasePath = "/pdf-tools";
    } else if (esPath.startsWith("/pdf-tools/")) {
      const esSlug = esPath.replace("/pdf-tools/", "");
      enBasePath = `/pdf-tools/${getEnPdfSlugForEsSlug(esSlug)}`;
    } else if (esPath === "/sobre-nosotros" || esPath === "/acerca-de") {
      enBasePath = "/about";
    } else if (esPath === "/contacto") {
      enBasePath = "/contact";
    } else if (esPath === "/politica-de-privacidad") {
      enBasePath = "/privacy-policy";
    } else if (esPath === "/terminos-de-uso") {
      enBasePath = "/terms-of-use";
    } else if (esPath === "/aviso-legal") {
      enBasePath = "/disclaimer";
    } else {
      const esSlug = esPath.replace(/^\//, "");
      enBasePath = `/${getEnSlugForEsSlug(esSlug)}`;
    }
  } else if (path.startsWith("/pt-br")) {
    const ptPath = path.replace(/^\/pt-br/, "") || "/";
    if (ptPath === "/" || ptPath === "") {
      enBasePath = "/";
    } else if (ptPath === "/calculators") {
      enBasePath = "/calculators";
    } else if (ptPath.startsWith("/calculators/")) {
      const ptSlug = ptPath.replace("/calculators/", "");
      enBasePath = `/calculators/${getEnCalculatorSlugForPtBrSlug(ptSlug)}`;
    } else if (ptPath === "/pdf-tools") {
      enBasePath = "/pdf-tools";
    } else if (ptPath.startsWith("/pdf-tools/")) {
      const ptSlug = ptPath.replace("/pdf-tools/", "");
      enBasePath = `/pdf-tools/${getEnPdfSlugForPtBrSlug(ptSlug)}`;
    } else if (ptPath === "/sobre-o-picamini" || ptPath === "/sobre-nos") {
      enBasePath = "/about";
    } else if (ptPath === "/contato") {
      enBasePath = "/contact";
    } else if (ptPath === "/politica-de-privacidade") {
      enBasePath = "/privacy-policy";
    } else if (ptPath === "/termos-de-uso") {
      enBasePath = "/terms-of-use";
    } else if (ptPath === "/aviso-legal") {
      enBasePath = "/disclaimer";
    } else {
      const ptSlug = ptPath.replace(/^\//, "");
      enBasePath = `/${getEnSlugForPtBrSlug(ptSlug)}`;
    }
  } else if (path.startsWith("/fr")) {
    const frPath = path.replace(/^\/fr/, "") || "/";
    if (frPath === "/" || frPath === "") {
      enBasePath = "/";
    } else if (frPath === "/calculators") {
      enBasePath = "/calculators";
    } else if (frPath.startsWith("/calculators/")) {
      const frSlug = frPath.replace("/calculators/", "");
      enBasePath = `/calculators/${getEnCalculatorSlugForFrSlug(frSlug)}`;
    } else if (frPath === "/pdf-tools") {
      enBasePath = "/pdf-tools";
    } else if (frPath.startsWith("/pdf-tools/")) {
      const frSlug = frPath.replace("/pdf-tools/", "");
      enBasePath = `/pdf-tools/${getEnPdfSlugForFrSlug(frSlug)}`;
    } else if (frPath === "/a-propos") {
      enBasePath = "/about";
    } else if (frPath === "/contact") {
      enBasePath = "/contact";
    } else if (frPath === "/politique-de-confidentialite") {
      enBasePath = "/privacy-policy";
    } else if (frPath === "/conditions-d-utilisation") {
      enBasePath = "/terms-of-use";
    } else if (frPath === "/mentions-legales") {
      enBasePath = "/disclaimer";
    } else {
      const frSlug = frPath.replace(/^\//, "");
      enBasePath = `/${getEnSlugForFrSlug(frSlug)}`;
    }
  }

  if (targetLang === "en") return enBasePath;

  if (targetLang === "es") {
    if (enBasePath === "/") return "/es/";
    if (enBasePath === "/calculators") return "/es/calculators";
    if (enBasePath.startsWith("/calculators/")) {
      const slug = enBasePath.replace("/calculators/", "");
      return `/es/calculators/${getEsCalculatorSlugForEnSlug(slug)}`;
    }
    if (enBasePath === "/pdf-tools") return "/es/pdf-tools";
    if (enBasePath.startsWith("/pdf-tools/")) {
      const slug = enBasePath.replace("/pdf-tools/", "");
      return `/es/pdf-tools/${getEsPdfSlugForEnSlug(slug)}`;
    }
    if (enBasePath === "/about") return "/es/sobre-nosotros";
    if (enBasePath === "/contact") return "/es/contacto";
    if (enBasePath === "/privacy-policy") return "/es/politica-de-privacidad";
    if (enBasePath === "/terms-of-use") return "/es/terminos-de-uso";
    if (enBasePath === "/disclaimer") return "/es/aviso-legal";

    const slug = enBasePath.replace(/^\//, "");
    return `/es/${getEsSlugForEnSlug(slug)}`;
  }

  if (targetLang === "de") {
    if (enBasePath === "/") return "/de/";
    if (enBasePath === "/calculators") return "/de/calculators";
    if (enBasePath.startsWith("/calculators/")) {
      const slug = enBasePath.replace("/calculators/", "");
      return `/de/calculators/${getDeCalculatorSlugForEnSlug(slug)}`;
    }
    if (enBasePath === "/pdf-tools") return "/de/pdf-tools";
    if (enBasePath.startsWith("/pdf-tools/")) {
      const slug = enBasePath.replace("/pdf-tools/", "");
      return `/de/pdf-tools/${getDePdfSlugForEnSlug(slug)}`;
    }
    if (enBasePath === "/about") return "/de/ueber-uns";
    if (enBasePath === "/contact") return "/de/kontakt";
    if (enBasePath === "/privacy-policy") return "/de/datenschutzrichtlinie";
    if (enBasePath === "/terms-of-use") return "/de/nutzungsbedingungen";
    if (enBasePath === "/disclaimer") return "/de/haftungsausschluss";

    const slug = enBasePath.replace(/^\//, "");
    return `/de/${getDeSlugForEnSlug(slug)}`;
  }

  if (targetLang === "pt-BR") {
    if (enBasePath === "/") return "/pt-br/";
    if (enBasePath === "/calculators") return "/pt-br/calculators";
    if (enBasePath.startsWith("/calculators/")) {
      const slug = enBasePath.replace("/calculators/", "");
      return `/pt-br/calculators/${getPtBrCalculatorSlugForEnSlug(slug)}`;
    }
    if (enBasePath === "/pdf-tools") return "/pt-br/pdf-tools";
    if (enBasePath.startsWith("/pdf-tools/")) {
      const slug = enBasePath.replace("/pdf-tools/", "");
      return `/pt-br/pdf-tools/${getPtBrPdfSlugForEnSlug(slug)}`;
    }
    if (enBasePath === "/about") return "/pt-br/sobre-o-picamini";
    if (enBasePath === "/contact") return "/pt-br/contato";
    if (enBasePath === "/privacy-policy") return "/pt-br/politica-de-privacidade";
    if (enBasePath === "/terms-of-use") return "/pt-br/termos-de-uso";
    if (enBasePath === "/disclaimer") return "/pt-br/aviso-legal";

    const slug = enBasePath.replace(/^\//, "");
    return `/pt-br/${getPtBrSlugForEnSlug(slug)}`;
  }

  if (targetLang === "fr") {
    if (enBasePath === "/") return "/fr/";
    if (enBasePath === "/calculators") return "/fr/calculators";
    if (enBasePath.startsWith("/calculators/")) {
      const slug = enBasePath.replace("/calculators/", "");
      return `/fr/calculators/${getFrCalculatorSlugForEnSlug(slug)}`;
    }
    if (enBasePath === "/pdf-tools") return "/fr/pdf-tools";
    if (enBasePath.startsWith("/pdf-tools/")) {
      const slug = enBasePath.replace("/pdf-tools/", "");
      return `/fr/pdf-tools/${getFrPdfSlugForEnSlug(slug)}`;
    }
    if (enBasePath === "/about") return "/fr/a-propos";
    if (enBasePath === "/contact") return "/fr/contact";
    if (enBasePath === "/privacy-policy") return "/fr/politique-de-confidentialite";
    if (enBasePath === "/terms-of-use") return "/fr/conditions-d-utilisation";
    if (enBasePath === "/disclaimer") return "/fr/mentions-legales";

    const slug = enBasePath.replace(/^\//, "");
    return `/fr/${getFrSlugForEnSlug(slug)}`;
  }

  return enBasePath;
}

export function LanguageSwitcher({ lang, currentPath }: { lang: Language; currentPath: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: { code: Language; label: string; name: string }[] = [
    { code: "en", label: "EN", name: "English" },
    { code: "es", label: "ES", name: "Español" },
    { code: "de", label: "DE", name: "Deutsch" },
    { code: "pt-BR", label: "PT", name: "Português" },
    { code: "fr", label: "FR", name: "Français" },
  ];

  const currentLang = languages.find((l) => l.code === lang) || languages[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-card border border-border/80 px-3 py-1.5 rounded-xl shadow-xs text-xs font-bold text-foreground hover:bg-secondary transition-colors"
        aria-label="Select Language"
      >
        <Globe className="size-4 text-primary" />
        <span>{currentLang.name}</span>
        <ChevronDown className={`size-3.5 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-44 rounded-xl border border-border bg-card p-1.5 shadow-lg z-50 animate-in fade-in-50 zoom-in-95">
          {languages.map((l) => {
            const isCurrent = lang === l.code;
            const targetUrl = getEquivalentUrl(currentPath, l.code);
            return (
              <a
                key={l.code}
                href={targetUrl}
                onClick={() => setIsOpen(false)}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  isCurrent
                    ? "bg-primary text-primary-foreground font-bold"
                    : "text-foreground hover:bg-secondary"
                }`}
              >
                <span>{l.name}</span>
                <span className={`text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded ${isCurrent ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  {l.label}
                </span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SiteHeader({ lang = "en" }: { lang?: Language }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const t = getTranslation(lang);

  const homeHref = lang === "de" ? "/de/" : lang === "es" ? "/es/" : lang === "pt-BR" ? "/pt-br/" : lang === "fr" ? "/fr/" : "/";
  const pdfHref = lang === "de" ? "/de/pdf-tools" : lang === "es" ? "/es/pdf-tools" : lang === "pt-BR" ? "/pt-br/pdf-tools" : lang === "fr" ? "/fr/pdf-tools" : "/pdf-tools";
  const spinWheelHref = lang === "de" ? "/de/zufallsrad-online" : lang === "es" ? "/es/ruleta-aleatoria" : lang === "pt-BR" ? "/pt-br/roleta-da-sorte-online" : lang === "fr" ? "/fr/roue-de-la-fortune-en-ligne" : "/spin-the-wheel";
  const wheelOfNamesHref = lang === "de" ? "/de/gluecksrad-namensauswahl" : lang === "es" ? "/es/ruleta-de-nombres" : lang === "pt-BR" ? "/pt-br/roleta-de-nomes" : lang === "fr" ? "/fr/roue-des-prenoms" : "/wheel-of-names";
  const yesNoWheelHref = lang === "de" ? "/de/ja-nein-rad" : lang === "es" ? "/es/ruleta-si-o-no" : lang === "pt-BR" ? "/pt-br/roleta-sim-ou-nao" : lang === "fr" ? "/fr/roue-oui-non" : "/yes-or-no-wheel";
  const qrGeneratorHref = lang === "de"
    ? "/de/pdf-tools/qr-code-generator"
    : lang === "es"
    ? "/es/pdf-tools/generador-codigo-qr"
    : lang === "pt-BR"
    ? "/pt-br/pdf-tools/gerador-de-codigo-qr"
    : lang === "fr"
    ? "/fr/pdf-tools/generateur-code-qr"
    : "/pdf-tools/qr-code";

  const calculatorsHref = lang === "de" ? "/de/calculators" : lang === "es" ? "/es/calculators" : lang === "pt-BR" ? "/pt-br/calculators" : lang === "fr" ? "/fr/calculators" : "/calculators";
  const calculatorsLabel = lang === "de" ? "Rechner" : lang === "es" ? "Calculadoras" : lang === "pt-BR" ? "Calculadoras" : lang === "fr" ? "Calculateurs" : "Calculators";

  const navLinks = [
    { label: t.home, href: homeHref, icon: LayoutGrid },
    { label: t.spinWheel, href: spinWheelHref, icon: Disc3 },
    { label: t.wheelOfNames, href: wheelOfNamesHref, icon: UserCheck },
    { label: "QR Generator", href: qrGeneratorHref, icon: QrCode },
  ];

  return (
    <header className="no-print sticky top-0 z-50 border-b border-border/80 bg-background/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1480px] items-center justify-between px-4 sm:px-6 lg:px-8 py-3">
        {/* Brand Logo */}
        <Link to={homeHref} className="flex items-center transition-opacity hover:opacity-90">
          <PicaminiLogo className="h-8 sm:h-9" />
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((item) => {
            const IconCmp = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <IconCmp className="size-4 text-primary/80" />
                {item.label}
              </Link>
            );
          })}

          {/* Desktop PDF Toolkit Pro Mega Menu (FreeConvert Style) */}
          <PdfMegaMenu lang={lang} />

          {/* Desktop Calculators Mega Menu (FreeConvert Style) */}
          <CalculatorsMegaMenu lang={lang} />
        </nav>

        {/* Language Switcher & Mobile Menu Toggle */}
        <div className="flex items-center gap-2">
          <LanguageSwitcher lang={lang} currentPath={currentPath} />

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="grid size-9 place-items-center rounded-xl border border-border bg-card text-foreground md:hidden hover:bg-secondary"
            aria-label="Toggle Navigation Menu"
          >
            {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Dropdown Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-b border-border bg-card px-4 pt-3 pb-5 space-y-3 animate-in slide-in-from-top duration-200">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2">
            {t.navigationAndTools}
          </p>
          <div className="grid gap-1">
            {navLinks.map((item) => {
              const IconCmp = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
                >
                  <span className="grid size-7 place-items-center rounded-lg bg-primary/10 text-primary">
                    <IconCmp className="size-4" />
                  </span>
                  {item.label}
                </Link>
              );
            })}

            {/* Mobile PDF Toolkit Accordion Submenu */}
            <PdfMobileMenu lang={lang} onClose={() => setMobileMenuOpen(false)} />

            {/* Mobile Calculators Accordion Submenu */}
            <CalculatorsMobileMenu lang={lang} onClose={() => setMobileMenuOpen(false)} />
          </div>
          <div className="pt-2 border-t border-border">
            <Link
              to={homeHref}
              onClick={() => setMobileMenuOpen(false)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground"
            >
              <Compass className="size-4" /> {t.exploreAllTools}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function SiteFooter({ lang = "en" }: { lang?: Language }) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const t = getTranslation(lang);

  const homeHref = lang === "de" ? "/de/" : lang === "es" ? "/es/" : lang === "pt-BR" ? "/pt-br/" : lang === "fr" ? "/fr/" : "/";
  const spinWheelHref = lang === "de" ? "/de/zufallsrad-online" : lang === "es" ? "/es/ruleta-aleatoria" : lang === "pt-BR" ? "/pt-br/roleta-da-sorte-online" : lang === "fr" ? "/fr/roue-de-la-fortune-en-ligne" : "/spin-the-wheel";
  const wheelOfNamesHref = lang === "de" ? "/de/gluecksrad-namensauswahl" : lang === "es" ? "/es/ruleta-de-nombres" : lang === "pt-BR" ? "/pt-br/roleta-de-nomes" : lang === "fr" ? "/fr/roue-des-prenoms" : "/wheel-of-names";
  const yesNoWheelHref = lang === "de" ? "/de/ja-nein-rad" : lang === "es" ? "/es/ruleta-si-o-no" : lang === "pt-BR" ? "/pt-br/roleta-sim-ou-nao" : lang === "fr" ? "/fr/roue-oui-non" : "/yes-or-no-wheel";
  const prizeWheelHref = lang === "de" ? "/de/gewinnrad-online" : lang === "es" ? "/es/ruleta-de-premios" : lang === "pt-BR" ? "/pt-br/roleta-de-premios" : lang === "fr" ? "/fr/roue-des-cadeaux" : "/prize-wheel";

  const numberWheelHref = lang === "de" ? "/de/zufallszahlen-generator-rad" : lang === "es" ? "/es/ruleta-de-numeros" : lang === "pt-BR" ? "/pt-br/roleta-de-numeros" : lang === "fr" ? "/fr/roue-des-nombres" : "/number-wheel";
  const foodWheelHref = lang === "de" ? "/de/was-soll-ich-essen-rad" : lang === "es" ? "/es/ruleta-de-comida" : lang === "pt-BR" ? "/pt-br/roleta-o-que-comer" : lang === "fr" ? "/fr/que-manger-roue" : "/food-wheel";
  const studentPickerHref = lang === "de" ? "/de/schueler-auslosung-generator" : lang === "es" ? "/es/selector-de-alumnos" : lang === "pt-BR" ? "/pt-br/sorteador-de-alunos" : lang === "fr" ? "/fr/tirage-au-sort-eleves" : "/random-student-picker";
  const teamGeneratorHref = lang === "de" ? "/de/gruppen-generator-zufall" : lang === "es" ? "/es/generador-de-equipos" : lang === "pt-BR" ? "/pt-br/gerador-de-equipes" : lang === "fr" ? "/fr/generateur-d-equipes-aleatoire" : "/team-generator";

  const aboutHref = lang === "de" ? "/de/ueber-uns" : lang === "es" ? "/es/sobre-nosotros" : lang === "pt-BR" ? "/pt-br/sobre-o-picamini" : lang === "fr" ? "/fr/a-propos" : "/about";
  const contactHref = lang === "de" ? "/de/kontakt" : lang === "es" ? "/es/contacto" : lang === "pt-BR" ? "/pt-br/contato" : lang === "fr" ? "/fr/contact" : "/contact";
  const privacyHref = lang === "de" ? "/de/datenschutzrichtlinie" : lang === "es" ? "/es/politica-de-privacidad" : lang === "pt-BR" ? "/pt-br/politica-de-privacidade" : lang === "fr" ? "/fr/politique-de-confidentialite" : "/privacy-policy";
  const termsHref = lang === "de" ? "/de/nutzungsbedingungen" : lang === "es" ? "/es/terminos-de-uso" : lang === "pt-BR" ? "/pt-br/termos-de-uso" : lang === "fr" ? "/fr/conditions-d-utilisation" : "/terms-of-use";
  const disclaimerHref = lang === "de" ? "/de/haftungsausschluss" : lang === "es" ? "/es/aviso-legal" : lang === "pt-BR" ? "/pt-br/aviso-legal" : lang === "fr" ? "/fr/mentions-legales" : "/disclaimer";

  return (
    <footer className="no-print mt-20 border-t border-border/80 bg-card py-12 text-sm text-muted-foreground">
      <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <PicaminiLogo className="h-7" />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t.footerDescription}
            </p>
            <div className="pt-1">
              <LanguageSwitcher lang={lang} currentPath={currentPath} />
            </div>
          </div>

          <div className="space-y-2.5">
            <p className="text-sm font-bold uppercase tracking-wider text-foreground">{t.popularWheels}</p>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link to={spinWheelHref} className="hover:text-foreground">{t.spinWheel}</Link>
              </li>
              <li>
                <Link to={wheelOfNamesHref} className="hover:text-foreground">{t.wheelOfNames}</Link>
              </li>
              <li>
                <Link to={yesNoWheelHref} className="hover:text-foreground">{t.yesNoWheel}</Link>
              </li>
              <li>
                <Link to={prizeWheelHref} className="hover:text-foreground">{t.prizeWheel}</Link>
              </li>
            </ul>
          </div>

          <div className="space-y-2.5">
            <p className="text-sm font-bold uppercase tracking-wider text-foreground">{t.decisionTools}</p>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link to={numberWheelHref} className="hover:text-foreground">{lang === "de" ? "Zufallszahlen Generator" : lang === "es" ? "Ruleta de Números" : lang === "pt-BR" ? "Roleta de Números" : lang === "fr" ? "Roue des Nombres" : "Random Number Wheel"}</Link>
              </li>
              <li>
                <Link to={foodWheelHref} className="hover:text-foreground">{lang === "de" ? "Was soll ich essen Rad" : lang === "es" ? "Ruleta de Comida" : lang === "pt-BR" ? "Roleta O Que Comer" : lang === "fr" ? "Roue Que Manger" : "What to Eat Food Wheel"}</Link>
              </li>
              <li>
                <Link to={studentPickerHref} className="hover:text-foreground">{lang === "de" ? "Schüler Auslosung" : lang === "es" ? "Selector de Alumnos" : lang === "pt-BR" ? "Sorteador de Alunos" : lang === "fr" ? "Tirage au Sort Élèves" : "Student Name Picker"}</Link>
              </li>
              <li>
                <Link to={teamGeneratorHref} className="hover:text-foreground">{lang === "de" ? "Gruppen Generator" : lang === "es" ? "Generador de Equipos" : lang === "pt-BR" ? "Gerador de Equipes" : lang === "fr" ? "Générateur d'Équipes" : "Team Generator"}</Link>
              </li>
            </ul>
          </div>

          <div className="space-y-2.5">
            <p className="text-sm font-bold uppercase tracking-wider text-foreground">
              {lang === "de" ? "Beliebte Rechner" : lang === "es" ? "Calculadoras Populares" : lang === "pt-BR" ? "Calculadoras Populares" : lang === "fr" ? "Calculateurs Populaires" : "Popular Calculators"}
            </p>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link to={lang === "en" ? "/calculators/percentage-calculator" : `/${lang === "pt-BR" ? "pt-br" : lang}/calculators/percentage-calculator`} className="hover:text-foreground">
                  {lang === "de" ? "Prozentrechner" : lang === "es" ? "Calculadora de Porcentaje" : lang === "pt-BR" ? "Calculadora de Porcentagem" : lang === "fr" ? "Calculateur de Pourcentage" : "Percentage Calculator"}
                </Link>
              </li>
              <li>
                <Link to={lang === "en" ? "/calculators/grade-calculator" : `/${lang === "pt-BR" ? "pt-br" : lang}/calculators/grade-calculator`} className="hover:text-foreground">
                  {lang === "de" ? "Notenrechner" : lang === "es" ? "Calculadora de Notas" : lang === "pt-BR" ? "Calculadora de Médias" : lang === "fr" ? "Calculateur de Moyenne" : "Grade Calculator"}
                </Link>
              </li>
              <li>
                <Link to={lang === "en" ? "/calculators/bmi-calculator" : `/${lang === "pt-BR" ? "pt-br" : lang}/calculators/bmi-calculator`} className="hover:text-foreground">
                  {lang === "de" ? "BMI-Rechner" : lang === "es" ? "Calculadora de IMC" : lang === "pt-BR" ? "Calculadora de IMC" : lang === "fr" ? "Calculateur d'IMC" : "BMI Calculator"}
                </Link>
              </li>
              <li>
                <Link to={lang === "en" ? "/calculators/mortgage-calculator" : `/${lang === "pt-BR" ? "pt-br" : lang}/calculators/mortgage-calculator`} className="hover:text-foreground">
                  {lang === "de" ? "Hypothekenrechner" : lang === "es" ? "Calculadora de Hipotecas" : lang === "pt-BR" ? "Calculadora de Financiamento" : lang === "fr" ? "Calculateur Prêt Immobilier" : "Mortgage Calculator"}
                </Link>
              </li>
              <li>
                <Link to={lang === "en" ? "/calculators" : `/${lang === "pt-BR" ? "pt-br" : lang}/calculators`} className="hover:text-foreground font-semibold text-primary">
                  {lang === "de" ? "Alle Rechner →" : lang === "es" ? "Todas las Calculadoras →" : lang === "pt-BR" ? "Todas as Calculadoras →" : lang === "fr" ? "Tous les calculateurs →" : "All Calculators →"}
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-2.5">
            <p className="text-sm font-bold uppercase tracking-wider text-foreground">{t.aboutEduzuma}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t.eduzumaDescription}
            </p>
            <a href="https://www.eduzuma.com/" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-blue-500 hover:text-blue-600 inline-block mt-1.5">
              {t.visitEduzuma}
            </a>
          </div>

          <div className="space-y-2.5">
            <p className="text-sm font-bold uppercase tracking-wider text-foreground">{t.aboutAndPolicies}</p>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link to={aboutHref} className="hover:text-foreground">{t.aboutUs}</Link>
              </li>
              <li>
                <Link to={contactHref} className="hover:text-foreground">{t.contactUs}</Link>
              </li>
              <li>
                <Link to={privacyHref} className="hover:text-foreground">{t.privacyPolicy}</Link>
              </li>
              <li>
                <Link to={termsHref} className="hover:text-foreground">{t.termsOfUse}</Link>
              </li>
              <li>
                <Link to={disclaimerHref} className="hover:text-foreground">{t.disclaimer}</Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t border-border/60 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} Picamini.online. {t.allRightsReserved}
        </div>
      </div>
    </footer>
  );
}

interface ToolShellProps {
  tool: ToolMeta | EsToolMeta | DeToolMeta | PtBrToolMeta | FrToolMeta;
  content: ToolContent;
  children: ReactNode;
  lang?: Language;
}

export function ToolShell({ tool, content, children, lang = "en" }: ToolShellProps) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const t = getTranslation(lang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const related = lang === "de"
    ? relatedDeTools(tool.slug)
    : lang === "es"
    ? relatedEsTools(tool.slug)
    : lang === "pt-BR"
    ? relatedPtBrTools(tool.slug)
    : lang === "fr"
    ? relatedFrTools(tool.slug)
    : relatedTools(tool.slug);

  const hubLink = lang === "de" ? "/de/" : lang === "es" ? "/es/" : lang === "pt-BR" ? "/pt-br/" : lang === "fr" ? "/fr/" : "/";
  const toolRoutePrefix = lang === "de" ? "/de/" : lang === "es" ? "/es/" : lang === "pt-BR" ? "/pt-br/" : lang === "fr" ? "/fr/" : "/";

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <SiteHeader lang={lang} />
      <main className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 pb-12">
        <nav className="no-print pt-6 text-sm">
          <Link
            to={hubLink}
            className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground font-semibold"
          >
            <ArrowLeft className="size-4" /> {t.freeToolsHub}
          </Link>
        </nav>

        {/* SEO Header Section */}
        <div className="no-print pt-4 pb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            {tool.category}
          </p>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl tracking-tight text-foreground">
            {content.h1Title || tool.name}
          </h1>
          <p className="mt-2.5 max-w-3xl text-base text-muted-foreground leading-relaxed">
            {content.intro}
          </p>
        </div>

        {/* Interactive Tool Component */}
        {children}

        {/* SEO Rich Content Sections */}
        <div className="no-print mt-14 space-y-10">
          {/* 1. What is section */}
          {content.whatIs && content.whatIs.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="size-5 text-primary" /> {t.whatIs} {tool.name}?
              </h2>
              <div className="space-y-4 text-muted-foreground text-sm sm:text-base leading-relaxed">
                {content.whatIs.map((p, idx) => (
                  <p key={idx} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </section>
          )}

          {/* 2. How to Use & Features */}
          <div className="grid gap-6 md:grid-cols-2">
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Sparkles className="size-5 text-primary" /> {t.howToUse} {tool.name}
              </h2>
              <ol className="space-y-3 text-sm sm:text-base text-muted-foreground">
                {content.howTo.map((step, i) => (
                  <li key={step} className="flex gap-3">
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary/10 text-primary font-bold text-xs">
                      {i + 1}
                    </span>
                    <span className="leading-snug">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <CheckCircle2 className="size-5 text-primary" /> {t.keyFeatures}
              </h2>
              <ul className="space-y-3 text-sm sm:text-base text-muted-foreground">
                {content.features.map((f) => (
                  <li key={f} className="flex gap-2.5 items-start">
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                    <span className="leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* 3. Benefits of Using */}
          {content.benefits && content.benefits.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground mb-4">
                {t.benefitsOfUsing} {tool.name}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {content.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-2.5 p-3 rounded-xl bg-secondary/40 border border-border/50">
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm font-medium text-foreground leading-snug">{benefit}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 4. Pro Tips for Classroom & Group Use */}
          {content.proTips && content.proTips.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-primary/5 border border-primary/20">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Sparkles className="size-5 text-primary" /> {t.proTips}
              </h2>
              <ul className="grid gap-3 sm:grid-cols-2">
                {content.proTips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 p-3 rounded-xl bg-card border border-border/50">
                    <span className="grid size-5 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground font-bold text-xs mt-0.5">
                      💡
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-foreground leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* 5. Use Cases / Who Should Use */}
          {content.useCases && content.useCases.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Users className="size-5 text-primary" /> {t.whoShouldUse}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {content.useCases.map((uc, idx) => (
                  <div key={idx} className="p-4 rounded-xl border bg-secondary/20">
                    <h3 className="font-bold text-sm text-foreground mb-1">{uc.role}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{uc.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 6. Comprehensive In-Depth Guide */}
          {content.detailedGuide && content.detailedGuide.length > 0 && (
            <div className="space-y-8">
              {content.detailedGuide.map((guideSection, gIdx) => (
                <section key={gIdx} className="surface-card p-6 sm:p-8 rounded-2xl bg-card border space-y-3.5">
                  <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
                    {guideSection.heading}
                  </h2>
                  <div className="space-y-3.5 text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {guideSection.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} dangerouslySetInnerHTML={{ __html: p }} />
                    ))}
                  </div>
                  {guideSection.bulletPoints && guideSection.bulletPoints.length > 0 && (
                    <ul className="space-y-2 pt-2">
                      {guideSection.bulletPoints.map((bp, bpIdx) => (
                        <li key={bpIdx} className="flex items-start gap-2.5">
                          <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                          <span className="text-sm sm:text-base text-foreground font-medium">{bp}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>
          )}

          {/* 7. Feature Comparison Table */}
          {content.comparisonTable && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border overflow-hidden">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-4">
                {content.comparisonTable.title}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-muted-foreground border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-secondary/50">
                      {content.comparisonTable.headers.map((h, hIdx) => (
                        <th key={hIdx} className="p-3 font-bold text-foreground">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {content.comparisonTable.rows.map((row, rIdx) => (
                      <tr key={rIdx} className="hover:bg-secondary/20 transition-colors">
                        <td className="p-3 font-medium text-foreground">{row.feature}</td>
                        <td className="p-3 font-semibold text-primary">{row.pickamini || row.eduzuma}</td>
                        <td className="p-3">{row.competitor}</td>
                        <td className="p-3">{row.paper}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* 8. Creative Classroom Ideas & Templates */}
          {content.ideasAndTemplates && content.ideasAndTemplates.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Sparkles className="size-5 text-primary" /> {lang === "de" ? "Kreative Ideen & Einsatzmöglichkeiten" : lang === "es" ? "Ideas y Dinámicas para el Aula" : "Creative Classroom & Activity Templates"}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {content.ideasAndTemplates.map((idea, iIdx) => (
                  <div key={iIdx} className="p-4 rounded-xl border bg-secondary/30 space-y-2">
                    <h3 className="font-bold text-sm text-foreground flex items-center gap-1.5">
                      <span className="text-primary">✦</span> {idea.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {idea.description}
                    </p>
                    <div className="flex flex-wrap gap-1 pt-1">
                      {idea.exampleItems.map((item, itemIdx) => (
                        <span
                          key={itemIdx}
                          className="px-2 py-0.5 rounded-md bg-card border text-[11px] font-medium text-foreground"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 9. Mathematical Probability */}
          {content.probabilityMath && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-primary/5 border border-primary/20 space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
                {content.probabilityMath.title}
              </h2>
              <div className="p-3.5 rounded-xl bg-card border font-mono text-center text-sm font-bold text-primary">
                {content.probabilityMath.formula}
              </div>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {content.probabilityMath.explanation}
              </p>
              <div className="grid gap-3 sm:grid-cols-2 pt-2">
                {content.probabilityMath.keyConcepts.map((kc, kcIdx) => (
                  <div key={kcIdx} className="p-3.5 rounded-xl bg-card border border-border/60">
                    <p className="font-bold text-xs sm:text-sm text-foreground mb-1">
                      {kc.concept}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {kc.detail}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 10. FAQ Section */}
          <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
              <HelpCircle className="size-5 text-primary" /> {t.faq}
            </h2>
            <dl className="space-y-4 divide-y divide-border/60">
              {content.faq.map((item, idx) => (
                <div key={item.q} className={idx > 0 ? "pt-4" : ""}>
                  <dt className="font-bold text-sm sm:text-base text-foreground" dangerouslySetInnerHTML={{ __html: item.q }} />
                  <dd className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: item.a }} />
                </div>
              ))}
            </dl>
          </section>

          {/* 11. Related Tools */}
          <section className="pt-4">
            <h2 className="text-xl font-bold text-foreground mb-4">{t.relatedTools}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r) => (
                <a
                  key={r.slug}
                  href={`${toolRoutePrefix}${r.slug}`}
                  className="surface-card p-4 rounded-xl border transition-all hover:-translate-y-0.5 hover:shadow-lift"
                >
                  <p className="font-bold text-sm text-foreground">{r.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{r.tagline}</p>
                </a>
              ))}
            </div>
          </section>
        </div>
      </main>
      <SiteFooter lang={lang} />
    </div>
  );
}
